import * as React from "react";
import {cx} from "emotion";

import "./address-modal-base.scss";

import {cs}         from "@Common/react/chain-services";
import {Form2}      from "@Common/react/cs-form-2/form2";
import {required}   from "@Common/react/cs-form-2/validators/required";
import {keyed}      from "@Common/react/keyed";
import {UseState}   from "@Common/react/use-state";
import {upperCase1} from "@Common/utils/strings";

import {ComponentLink}           from "../../../../react/cst2";
import {Input}                   from "../../common/design2/form-ui/input/input";
import {DeprecatedModalService2} from "../../common/design2/modal-service/modal-service";
import {Select}                  from "../../common/design2/select/select";

const usStates = require("../../common/us-states.json");
const canadaStates = require("../../common/canada-provinces.json");

export const AddressModalBase = ({title=undefined, fields, next: rootNext}:{
    title  ?: string,
    fields  : Array<{path: string, label: string, required?: boolean}>,
    next    : ComponentLink<typeof DeprecatedModalService2<{
        actionName  : string,
        onConfirm   : (address: Record<string, string>, action: string) => Promise<void | {
            address              : Record<string, string>,
            validationMessage   ?: string,
        }>,
        countryCode : string,
        initData   ?: Record<string, string>,
        controls   ?: Array<{
            label   : string,
            onClick : (ctx:{closeModal: () => void}) => void,
        }>,
    }>>,
}) => cs(
    ["modal", ({}, next) => DeprecatedModalService2({
        top: 20,
        render: ({actionName, onConfirm, countryCode, initData, controls}, rm) => ({
            size: "mini",
            content: next({
                actionName, onConfirm, initData, countryCode, controls,
                close: () => rm(),
            }),
        }),
        xdQaName: "Address",
        next: rootNext,
    })],
    ["data", ({modal}, next) => UseState({initValue: modal.initData, next})],
    ["apiError", ({data}, next) => UseState({initValue: data.value?.validationMessage, next})],
    ["action", ({modal}, next) => UseState({initValue: modal.actionName, next})],
    ["form", ({data, modal, action, apiError}, next) => Form2({
        data,
        fields: [
            ...fields.map((field) => ({
                path: [field.path],
                validators: field.required ? [required] : [],
            })),
            {
                path: ["city"],
                validators: [required],
            },
            {
                path: ["state"],
                validators: [required],
            },
            {
                path: ["zip"],
                validators: [required],
            },
        ],
        onSubmit: async () => {
            const result = await modal.onConfirm(data.value, action.value);
            if( result?.validationMessage )
            {
                apiError.onChange(result.validationMessage);
                data.onChange({
                    ...result.address
                });
                action.onChange('edit');
            }
            else
            {
                modal.close();
            }
        },
        next,
    })],
    ({modal, form, action, apiError}) => {
        const field = ({path, label, renderControl}) => {
            const field = form.field([path]);
            return (
                <div className="form-group">
                    <div 
                        className={cx("control-label", {required: field.validators.find((v) => v.name === "required")})}
                        xd-qa-type="label"
                        xd-qa-name={upperCase1(action.value)+" Address "+label}
                    >
                        {label}
                    </div>
                    {renderControl(field)}
                </div>
            );
        };

        const fieldInput = ({path, label}) => (
            field({
                path, label,
                renderControl: (field) => (
                    Input({
                        className: cx({hasError: field.error}),
                        state: field.state,
                        xdQaName: upperCase1(action.value)+" Address "+label,
                    })
                )
            })
        );
        return (
            <div className="address-modal-base-84d">
                <div className="header">
                    <div className="title" xd-qa-type="label" xd-qa-name={upperCase1(action.value)+" Address"}>
                        {title ?? `${upperCase1(action.value)} Address`}
                    </div>
                </div>

                <div className="content">

                    {fields.map((field, i) => cs(
                        keyed(i),
                        () => fieldInput({label: field.label, path: field.path}),
                    ))}

                    {fieldInput({label: "City", path: "city"})}

                    <div className="state-zip">

                        {field({
                            label: modal.countryCode === "CA" ? "Province" : "State", path: "state",
                            renderControl: (field) => (
                                Select({
                                    style: {
                                        color: field.error && "danger",
                                    },
                                    list: modal.countryCode === "CA" ? canadaStates : usStates,
                                    itemToLabel: (s) => s.name,
                                    onChange: (s) => field.state.onChange(s.abbreviation),
                                    isSelected: (s) => field.state.value === s.abbreviation,
                                    placeholder: "Select",
                                    xdQaName: upperCase1(action.value)+" Address "+(modal.countryCode === "CA" ? "Province" : "State")
                                })
                            )
                        })}

                        {fieldInput({label: "Zip Code", path: "zip"})}
                    </div>

                    {apiError.value && (
                        <div className="error">
                            {apiError.value}
                        </div>
                    )}
                </div>

                <div className="footer">
                    <button {...{
                        className: "cancel",
                        onClick: () => modal.close(),
                        "xd-qa-type": "button",
                        "xd-qa-name": upperCase1(action.value)+" Address Cancel",
                    }}>
                        Cancel
                    </button>
                    {modal.controls?.map((control, i) => (
                        <button {...{
                            className: "extra",
                            onClick: () => {
                                control.onClick({closeModal: () => modal.close()})
                            },
                            key: i,
                        }}>
                            {control.label}
                        </button>
                    ))}
                    {cs(
                        ["submitting", (_, next) => UseState({next})],
                        ({submitting}) => (
                            <button {...{
                                className: cx("next", {disabled: !form.valid}),
                                disabled: submitting.value,
                                onClick: async () => {
                                    apiError.onChange(null);
                                    submitting.onChange(true);
                                    try {
                                        await form.submit();
                                    } catch (e) {
                                        apiError.onChange(e);
                                    } finally {
                                        submitting.onChange(false);
                                    }
                                },
                                "xd-qa-type": "button",
                                "xd-qa-name": upperCase1(action.value)+" Address "+ (action.value === "edit" ? "Update" : upperCase1(action.value)),
                            }}>
                                {action.value === "edit" ? (
                                    "Update"
                                ) : (
                                    upperCase1(action.value)
                                )}
                            </button>
                        ),
                    )}
                </div>
            </div>
        );
    },
);
